import React, { useState, useRef, useEffect } from 'react'
import { UploadIcon } from '../assetsComponents'
import classNames from 'classnames'
import API from '../../utils/API'
import axios from 'axios'
import {
  useErrorService,
  useNotificationService,
  useUserService,
} from '@abroad/components'
import ImagePlacholder from '../assetsComponents/ImagePlacholder'
import UnsavedDocumentModal from './UnsavedDocumentModal'
import { gtmUploadExperienceHeadshotAction } from './gtmQuestUploadFunctions'

const UploadHeadshot = ({ questId }) => {
  const { user } = useUserService()
  const [uploadedHeadshotDocs, setUploadedHeadshotDocs] = useState([])
  const [isLoadingPage, setIsLoadingPage] = useState(true)
  const [headshotData, setHeadshotData] = useState()
  const [uploadStatus, setUploadStatus] = useState('idle') // idle | uploading | uploaded | error
  const [uploadProgress, setUploadProgress] = useState(0)
  const [currentPreviewUrl, setCurrentPreviewUrl] = useState('')
  const [currentFileName, setCurrentFileName] = useState('')
  const [dragActive, setDragActive] = useState(false)
  const inputRef = useRef(null)
  const { showError } = useErrorService()
  const Notification = useNotificationService()
  const userId = user?.id
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [pendingDeleteFile, setPendingDeleteFile] = useState('')

  // Fetch headshot data on mount
  useEffect(() => {
    const getQuestHeadshotData = async () => {
      if (userId && questId) {
        try {
          const { data } = await API.quests.getTravelDocument(questId, userId)
          setHeadshotData(data)
          if (
            data &&
            data?.travelDocuments &&
            data?.travelDocuments?.length > 0
          ) {
            const headshotDocs = data?.travelDocuments?.filter(
              (doc) => doc.documentType === 'headshot',
            )
            setUploadedHeadshotDocs(headshotDocs || [])
          } else {
            setUploadedHeadshotDocs([])
          }
        } catch (error) {
          showError(error)
          setUploadedHeadshotDocs([])
        } finally {
          setIsLoadingPage(false)
        }
      } else {
        setIsLoadingPage(false)
      }
    }
    if (user !== undefined) {
      getQuestHeadshotData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questId, userId])

  // Fetch preview URLs for all uploaded headshot files
  useEffect(() => {
    const fetchAllPreviews = async () => {
      if (!uploadedHeadshotDocs.length || !userId || !questId) return
      let previews = {}
      for (const doc of uploadedHeadshotDocs) {
        for (const fileName of doc.fileName) {
          try {
            const { data } = await API.quests.downloadTravelDocument(
              questId,
              userId,
              fileName,
            )
            if (data && data.url) {
              previews[fileName] = data.url
            }
          } catch (e) {
            showError(e)
          }
        }
      }
      // Show the latest uploaded image preview
      const allFileNames = Object.keys(previews)
      if (allFileNames.length > 0) {
        setCurrentPreviewUrl(previews[allFileNames[allFileNames.length - 1]])
        setCurrentFileName(allFileNames[allFileNames.length - 1])
        setUploadStatus('uploaded')
      } else {
        setCurrentPreviewUrl('')
        setCurrentFileName('')
        setUploadStatus('idle')
      }
    }
    fetchAllPreviews()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedHeadshotDocs, userId, questId])

  // Handle file selection (by input or drag)
  const handleFile = async (file) => {
    if (uploadStatus === 'uploading') return
    if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
      Notification.showNotification('Please upload only JPG, PNG, or PDF files')
      return
    }
    setUploadStatus('uploading')
    setUploadProgress(0)
    setCurrentPreviewUrl('')
    setCurrentFileName('')
    try {
      // Step 1: Get signed URL
      const contentType = file.name
        .substr(file.name.lastIndexOf('.') + 1)
        .toUpperCase()
      const documentType = 'headshot'
      const { data: signedUrlData } =
        await API.quests.getTravelDocumentSignedURL(
          questId,
          contentType,
          documentType,
        )
      if (signedUrlData && signedUrlData.url) {
        // Step 2: Upload to signed URL
        await axios.put(signedUrlData.url.url, file, {
          headers: {
            'Content-Type': signedUrlData.url.contentType,
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percent = Math.round(
                (progressEvent.loaded * 95) / progressEvent.total,
              )
              setUploadProgress(percent)
            }
          },
        })
        setUploadProgress(100)
        // Step 3: Finalize upload
        if (userId) {
          const { data: uploadApiResult } =
            await API.quests.uploadTravelDocument(questId, userId, {
              fileName: signedUrlData.fileName,
              documentType: 'headshot',
            })
          if (uploadApiResult && uploadApiResult?.travelDocuments) {
            const headshotDocs = uploadApiResult?.travelDocuments?.filter(
              (doc) => doc.documentType === 'headshot',
            )
            setUploadedHeadshotDocs(headshotDocs || [])
            // The preview will be set by the useEffect above

            // Track GTM event for successful upload
            gtmUploadExperienceHeadshotAction(
              questId,
              signedUrlData.fileName,
              file.type,
              userId,
            )
          }
        } else {
          throw new Error('User session error. Please try logging in again.')
        }
      } else {
        throw new Error('Could not get signed URL.')
      }
    } catch (e) {
      Notification.showNotification('Upload failed. Please try again.')
      setUploadStatus('error')
    }
  }

  // Input change handler
  const handleChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFile(e.target.files[0])
    }
  }

  // Drag and drop handlers
  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }
  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0])
    }
  }

  // Delete uploaded image
  const handleDelete = async (fileName) => {
    const documentType = 'headshot'
    try {
      await API.quests.deleteTravelDocument(
        questId,
        userId,
        fileName,
        documentType,
      )
      setUploadedHeadshotDocs((currentDocs) => {
        const updatedDocs = currentDocs
          .map((doc) => ({
            ...doc,
            fileName: doc.fileName.filter((f) => f !== fileName),
          }))
          .filter((doc) => doc.fileName.length > 0)
        return updatedDocs
      })
      setCurrentPreviewUrl('')
      setCurrentFileName('')
      setUploadStatus('idle')
    } catch (error) {
      showError(error)
    }
  }

  const handleDeleteIconClick = (fileName) => {
    setPendingDeleteFile(fileName)
    setShowDeleteModal(true)
  }
  const handleDeleteConfirm = async () => {
    if (pendingDeleteFile) {
      await handleDelete(pendingDeleteFile)
      setPendingDeleteFile('')
      setShowDeleteModal(false)
    }
  }
  const handleDeleteCancel = () => {
    setPendingDeleteFile('')
    setShowDeleteModal(false)
  }

  const onButtonClick = () => {
    if (uploadStatus !== 'uploading') inputRef.current.click()
  }

  const processHtmlContent = (htmlContent) => {
    if (!htmlContent) return ''

    // Replace <strong> tags with <strong> having s6 class
    let processedContent = htmlContent.replace(
      /<strong>/g,
      '<strong class="s6">',
    )

    // Wrap the entire content in a div with s8 class for non-strong elements
    processedContent = `<div class="s8 mb-3">${processedContent}</div>`

    return processedContent
  }

  return (
    <div>
      {isLoadingPage ? (
        <div className='text-center mt-40px'>
          <div className='spinner-border text-saffron-700' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </div>
        </div>
      ) : (
        <>
          <div className='d-flex justify-content-center align-items-center mt-4'>
            {headshotData?.stepConfiguration?.step3?.subStep4?.description && (
              <div
                className='s8 text-start'
                dangerouslySetInnerHTML={{
                  __html: processHtmlContent(
                    headshotData.stepConfiguration.step3.subStep4.description,
                  ),
                }}
              />
            )}
          </div>

          {/* Image preview above upload box */}
          {uploadStatus === 'uploaded' && currentPreviewUrl && (
            <div className='row mt-3 mb-3'>
              <div className='col-lg-3 col-md-4 col-12 mb-3'>
                <div className='p-2 bg-gray-100 border-radius-5 position-relative pb-3'>
                  <img
                    className='border-radius-5'
                    src={currentPreviewUrl}
                    alt='headshot img'
                    style={{
                      width: '100%',
                      height: '240px',
                      aspectRatio: '0.8',
                      objectFit: 'cover',
                      objectPosition: 'center top',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      background: '#f8f9fa',
                    }}
                  />
                  <div
                    className='position-absolute top-12px right-12px'
                    onClick={() => handleDeleteIconClick(currentFileName)}
                    style={{ cursor: 'pointer' }}>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='24'
                      height='24'
                      viewBox='0 0 24 24'
                      fill='none'>
                      <rect
                        x='24'
                        y='24'
                        width='24'
                        height='24'
                        rx='12'
                        transform='rotate(-180 24 24)'
                        fill='#EBEBEB'
                      />
                      <path
                        d='M17 7L7 17'
                        stroke='black'
                        strokeLinecap='round'
                      />
                      <path
                        d='M17 17L7 7'
                        stroke='black'
                        strokeLinecap='round'
                      />
                    </svg>
                  </div>
                  <div className='s8 mt-3'>
                    {currentFileName.length > 30
                      ? currentFileName.slice(0, 30) + '...'
                      : currentFileName}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Progress bar while uploading */}
          {uploadStatus === 'uploading' && (
            <div className='row mt-3 mb-3'>
              <div className='col-lg-3 col-md-4 col-12 mb-3'>
                <div className='p-2 bg-gray-100 border-radius-5 position-relative pb-3'>
                  <div
                    className='border-radius-5 d-flex align-items-center justify-content-center w-100'
                    style={{
                      height: '240px',
                      background: '#f8f9fa',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                    }}>
                    {/* Placeholder for image area while uploading */}
                    <ImagePlacholder />
                  </div>
                  {/* Progress bar below image area */}
                  <div className='w-100 mt-3'>
                    <div
                      className='w-100 bg-white border-radius-50'
                      style={{
                        height: '8px',
                      }}>
                      <div
                        className='h-100'
                        style={{
                          width: `${uploadProgress}%`,
                          background: '#EFC030',
                          transition: 'width 0.2s',
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className='s7 mb-2'>Upload Photo</label>
            <form
              id='fileUploadForm'
              className={classNames(
                'upload-zone py-3 text-center position-relative cursor-pointer',
                {
                  'drag-active': dragActive,
                },
              )}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={onButtonClick}>
              <input
                ref={inputRef}
                type='file'
                id='fileUploadInput'
                className='d-none'
                accept='image/jpeg,image/png'
                onChange={handleChange}
                disabled={uploadStatus === 'uploading'}
              />
              <div className='mb-3'>
                <UploadIcon />
              </div>
              <p className='s7 mb-2'>
                Choose a file{' '}
                <span className='opacity-50 cursor-pointer'>or</span> drag it
                here
              </p>
              <div className='s9 opacity-50'>Supported Format: JPG, PNG</div>
            </form>
          </div>
          {/* Delete confirmation modal */}
          <UnsavedDocumentModal
            showModal={showDeleteModal}
            handleClose={handleDeleteCancel}
            handleLeave={handleDeleteConfirm}
            modalTitle='Delete File'
            modalBody='Are you sure you want to delete this file? This action cannot be undone.'
            cancelLabel='Cancel'
            confirmLabel='Delete'
          />
        </>
      )}
    </div>
  )
}

export default UploadHeadshot
